import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  useTheme,
  Box,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  styled
} from '@mui/material';
import {
  Timeline,
  Send,
  Dns,
  ScheduleSend,
  People,
  DataUsage,
  Security,
  NotificationsActive,
  CloudDone,
  TrendingUp,
  MoreVert,
  ArrowDropUp,
  FiberManualRecord,
  Campaign,
  InsertChart,
  Storage
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

const DashboardCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: theme.shadows[4],
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8]
  }
}));

const StyledProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: theme.palette.action.hover,
  '& .MuiLinearProgress-bar': {
    borderRadius: 4,
    transition: 'transform 0.4s ease'
  }
}));

const Dashboard = () => {
  const theme = useTheme();
  const messagesSent = 2157;
  const activeAccounts = 12;
  const proxyStatus = { valid: 8, invalid: 2 };

  // Data arrays
  const metrics = [
    { 
      title: 'Active Sessions', 
      value: activeAccounts,
      icon: <People fontSize="inherit" />,
      color: theme.palette.primary.main,
      trend: 12.5,
      subText: '3 operators online'
    },
    {
      title: 'Messages Today',
      value: messagesSent,
      icon: <Send fontSize="inherit" />,
      color: theme.palette.secondary.main,
      trend: 24.3,
      subText: '142 sent this hour'
    },
    {
      title: 'Proxy Health',
      value: `${proxyStatus.valid}/${proxyStatus.valid + proxyStatus.invalid}`,
      icon: <CloudDone fontSize="inherit" />,
      color: theme.palette.success.main,
      trend: 8.1,
      subText: '92% success rate'
    },
    {
      title: 'Active Campaigns',
      value: 18,
      icon: <Campaign fontSize="inherit" />,
      color: theme.palette.warning.main,
      trend: -3.2,
      subText: '5 completing now'
    }
  ];

  const messageData = [
    { hour: '00:00', count: 45 }, { hour: '04:00', count: 32 },
    { hour: '08:00', count: 87 }, { hour: '12:00', count: 142 },
    { hour: '16:00', count: 98 }, { hour: '20:00', count: 76 }
  ];

  const systemHealth = [
    { metric: 'API Response', value: '128ms', status: 'good', icon: <Storage /> },
    { metric: 'Server Load', value: '64%', status: 'warning', icon: <DataUsage /> },
    { metric: 'DB Connections', value: '28/50', status: 'good', icon: <Security /> }
  ];

  const activeUsers = [
    { id: 1, name: 'Admin User', role: 'admin', activity: 'Scheduling posts', lastActive: '2m ago' },
    { id: 2, name: 'Operator 1', role: 'operator', activity: 'Sending messages', lastActive: '5m ago' },
    { id: 3, name: 'Analyst 1', role: 'analyst', activity: 'Viewing reports', lastActive: '10m ago' }
  ];

  const campaigns = [
    { name: 'Product Launch', progress: 75, status: 'active', members: 8 },
    { name: 'Market Research', progress: 42, status: 'pending', members: 5 },
    { name: 'User Onboarding', progress: 90, status: 'completed', members: 12 }
  ];

  return (
    <Box sx={{ p: 3, backgroundColor: theme.palette.background.default }}>
      <Grid container spacing={3}>
        {/* Key Metrics Row */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {metrics.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <DashboardCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Avatar sx={{ 
                        bgcolor: `${stat.color}15`,
                        color: stat.color,
                        width: 44,
                        height: 44
                      }}>
                        {stat.icon}
                      </Avatar>
                      <IconButton size="small">
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 1 }}>
                      <Typography variant="h4" sx={{ mr: 1 }}>{stat.value}</Typography>
                      <Chip 
                        label={`${stat.trend}%`}
                        size="small"
                        icon={<ArrowDropUp />}
                        sx={{ 
                          backgroundColor: `${stat.trend > 0 ? theme.palette.success.light : theme.palette.error.light}30`,
                          color: stat.trend > 0 ? theme.palette.success.main : theme.palette.error.main
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {stat.subText}
                    </Typography>
                  </CardContent>
                </DashboardCard>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Main Content Area */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {/* Message Throughput */}
            <Grid item xs={12}>
              <DashboardCard>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6">Message Throughput</Typography>
                    <Chip 
                      label="Live Data"
                      color="primary"
                      icon={<NotificationsActive fontSize="small" />}
                    />
                  </Box>
                  <Box sx={{ height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={messageData}>
                        <defs>
                          <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.2}/>
                            <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis 
                          dataKey="hour" 
                          tick={{ fill: theme.palette.text.secondary }}
                          axisLine={false}
                        />
                        <YAxis 
                          tick={{ fill: theme.palette.text.secondary }}
                          axisLine={false}
                        />
                        <Tooltip 
                          contentStyle={{
                            background: theme.palette.background.paper,
                            border: 'none',
                            borderRadius: '12px',
                            boxShadow: theme.shadows[3]
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="count"
                          stroke={theme.palette.primary.main}
                          fill="url(#colorUv)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </DashboardCard>
            </Grid>

            {/* Campaign Progress */}
            <Grid item xs={12}>
              <DashboardCard>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Active Campaigns</Typography>
                  {campaigns.map((campaign, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1">{campaign.name}</Typography>
                        <Chip 
                          label={campaign.status}
                          size="small"
                          sx={{
                            backgroundColor: 
                              campaign.status === 'active' ? `${theme.palette.primary.main}20` :
                              campaign.status === 'completed' ? `${theme.palette.success.main}20` :
                              `${theme.palette.warning.main}20`,
                            color: 
                              campaign.status === 'active' ? theme.palette.primary.main :
                              campaign.status === 'completed' ? theme.palette.success.main :
                              theme.palette.warning.main
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <StyledProgress 
                          variant="determinate" 
                          value={campaign.progress}
                          sx={{
                            flexGrow: 1,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: 
                                campaign.status === 'active' ? theme.palette.primary.main :
                                campaign.status === 'completed' ? theme.palette.success.main :
                                theme.palette.warning.main
                            }
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {campaign.progress}%
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </DashboardCard>
            </Grid>
          </Grid>
        </Grid>

        {/* Right Sidebar */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={3}>
            {/* System Health */}
            <Grid item xs={12}>
              <DashboardCard>
                <CardContent>
                  <Typography variant="h6" gutterBottom>System Health</Typography>
                  {systemHealth.map((metric, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      p: 2,
                      mb: 2,
                      borderRadius: '12px',
                      backgroundColor: theme.palette.background.paper,
                      boxShadow: theme.shadows[1]
                    }}>
                      <Avatar sx={{ 
                        bgcolor: `${theme.palette.primary.main}10`,
                        color: theme.palette.primary.main,
                        mr: 2,
                        width: 40,
                        height: 40
                      }}>
                        {metric.icon}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">{metric.metric}</Typography>
                        <Typography variant="body1" fontWeight="500">{metric.value}</Typography>
                      </Box>
                      <FiberManualRecord sx={{ 
                        color: metric.status === 'good' 
                          ? theme.palette.success.main 
                          : theme.palette.warning.main,
                        fontSize: '14px'
                      }}/>
                    </Box>
                  ))}
                </CardContent>
              </DashboardCard>
            </Grid>

            {/* Active Users */}
            <Grid item xs={12}>
              <DashboardCard>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Active Users</Typography>
                  <List disablePadding>
                    {activeUsers.map((user) => (
                      <ListItem key={user.id} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Avatar sx={{ 
                            width: 32, 
                            height: 32,
                            bgcolor: user.role === 'admin' 
                              ? `${theme.palette.primary.main}20`
                              : theme.palette.action.hover,
                            color: user.role === 'admin' 
                              ? theme.palette.primary.main 
                              : theme.palette.text.secondary
                          }}>
                            <Security fontSize="small" />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={user.name}
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography 
                                variant="caption"
                                sx={{ 
                                  mr: 1,
                                  color: user.role === 'admin' 
                                    ? theme.palette.primary.main 
                                    : theme.palette.text.secondary
                                }}
                              >
                                {user.role}
                              </Typography>
                              <FiberManualRecord sx={{ fontSize: 4, mx: 0.5 }} />
                              <Typography variant="caption" color="text.secondary">
                                {user.lastActive}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </DashboardCard>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;